// GAVION recruiting on Supabase: every RPC re-checks is_admin() (P0001 'forbidden'); the CV bucket job-cvs is read
// (signed URLs) and deleted by admins only (storage policies of 91_gavion_recruiting.sql), and written by admins into a
// new application's folder only (GAVION manual-candidates, 95_gavion_manual_candidates.sql).
import type { AiReview } from "../aiReview";
import type {
  ApplicationDetail,
  ApplicationNote,
  ApplicationSummary,
  Posting,
  PostingInput,
} from "../domain";
import type { CandidateFile, ManualCandidateInput } from "../manualCandidate";
import {
  CV_LINK_SECONDS,
  RecruitingApiError,
  type RecruitingClient,
} from "./types";

type RpcError = { code?: string; message?: string; details?: string } | null;
type StorageBucket = {
  createSignedUrl: (
    path: string,
    expiresIn: number,
  ) => PromiseLike<{ data: { signedUrl: string } | null; error: unknown }>;
  remove: (paths: string[]) => PromiseLike<{ error: unknown }>;
  upload: (
    path: string,
    body: Blob,
    options: { contentType: string; upsert: boolean },
  ) => PromiseLike<{ error: unknown }>;
};
export type RecruitingSupabase = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: RpcError }>;
  storage: { from: (bucket: string) => StorageBucket };
};

export const JOB_CV_BUCKET = "job-cvs";
const CODE = /^[a-z_]{3,40}$/;

/** P0001 messages are stable codes; 42501 (no EXECUTE / RLS) is forbidden; anything else is unknown. */
export const recruitingErrorCode = (e: RpcError): string => {
  if (!e) return "unknown";
  if (e.code === "42501") return "forbidden";
  return e.code === "P0001" && CODE.test(e.message ?? "")
    ? (e.message as string)
    : "unknown";
};

const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const BASE62 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
/** The random file name of 91_'s path rule (<uid>/<8–64 letters and digits>.<ext>): 24 characters. */
export const randomFileName = (): string =>
  Array.from(
    crypto.getRandomValues(new Uint8Array(24)),
    (b) => BASE62[b % 62],
  ).join("");

export const createSupabaseRecruitingClient = (
  getClient: () => RecruitingSupabase,
): RecruitingClient => {
  const call = async (fn: string, args?: Record<string, unknown>) => {
    const { data, error } = await getClient().rpc(fn, args);
    if (error)
      throw new RecruitingApiError(
        recruitingErrorCode(error),
        error.details ?? undefined,
      );
    return data;
  };
  return {
    async postings() {
      return arr<Posting>(await call("recruiting_postings"));
    },
    async savePosting(id, input: PostingInput) {
      return (await call("recruiting_save_posting", {
        p_id: id,
        p: input,
      })) as Posting;
    },
    async setPostingStatus(id, status) {
      return (await call("recruiting_set_posting_status", {
        p_id: id,
        p_status: status,
      })) as Posting;
    },
    async deletePosting(id) {
      await call("recruiting_delete_posting", { p_id: id });
    },
    async applications(f) {
      return arr<ApplicationSummary>(
        await call("recruiting_applications", {
          p_posting_id: f.postingId ?? null,
          p_spontaneous: f.spontaneous ?? false,
          p_status: f.status ?? null,
        }),
      );
    },
    async application(id) {
      return (await call("recruiting_application", {
        p_id: id,
      })) as ApplicationDetail;
    },
    async setApplicationStatus(id, status) {
      return (await call("recruiting_set_application_status", {
        p_id: id,
        p_status: status,
      })) as ApplicationDetail;
    },
    async addNote(id, text) {
      return (await call("recruiting_add_application_note", {
        p_id: id,
        p_text: text,
      })) as ApplicationNote;
    },
    async deleteApplication(id) {
      const r = (await call("recruiting_delete_application", {
        p_id: id,
      })) as { cv_path?: string | null; letter_path?: string | null } | null;
      // The purge queue removes the files within a day; this removes them now (admins may delete in job-cvs).
      const paths = [r?.cv_path, r?.letter_path].filter(
        (x): x is string => typeof x === "string" && x !== "",
      );
      if (paths.length)
        await Promise.resolve(
          getClient().storage.from(JOB_CV_BUCKET).remove(paths),
        ).catch(() => undefined);
    },
    async eraseByEmail(email, reason) {
      const r = (await call("erase_job_applications", {
        p_email: email,
        p_reason: reason,
      })) as { count?: number; purge_queued?: number } | null;
      return {
        count: Number(r?.count ?? 0),
        purgeQueued: Number(r?.purge_queued ?? 0),
      };
    },
    async newCount() {
      return Number((await call("recruiting_new_count")) ?? 0);
    },
    async cvUrl(path) {
      const { data, error } = await getClient()
        .storage.from(JOB_CV_BUCKET)
        .createSignedUrl(path, CV_LINK_SECONDS);
      if (error || !data?.signedUrl || !/^https?:\/\//i.test(data.signedUrl))
        throw new RecruitingApiError("cv_unavailable");
      return data.signedUrl;
    },
    async addApplication(input: ManualCandidateInput) {
      // the folder of the new application: the RPC refuses files outside it, the storage policy a folder in use
      const uid = crypto.randomUUID();
      const bucket = getClient().storage.from(JOB_CV_BUCKET);
      const uploaded: string[] = [];
      const put = async (f: CandidateFile | null, kind: "cv" | "letter") => {
        if (!f) return {};
        const path = `${uid}/${randomFileName()}.${f.ext}`;
        const { error } = await bucket.upload(path, f.file, {
          contentType: f.contentType,
          upsert: false,
        });
        if (error) throw new RecruitingApiError("file_upload_failed", kind);
        uploaded.push(path);
        return { [`${kind}_path`]: path, [`${kind}_filename`]: f.filename };
      };
      try {
        const files = {
          ...(await put(input.cv, "cv")),
          ...(await put(input.letter, "letter")),
        };
        return (await call("recruiting_add_application", {
          p: {
            uid,
            name: input.name,
            posting_id: input.posting_id,
            source: input.source,
            email: input.email,
            phone: input.phone,
            availability: input.availability,
            profile_url: input.profile_url,
            message: input.message,
            ...files,
          },
        })) as ApplicationDetail;
      } catch (e) {
        // no row: the files go now (else the orphan sweep of 91_ queues them after 2 days)
        if (uploaded.length)
          await Promise.resolve(bucket.remove(uploaded)).catch(() => undefined);
        throw e;
      }
    },
    async requestReview(id) {
      return (await call("recruiting_request_review", {
        p_application_id: id,
      })) as AiReview;
    },
  };
};
